import { amplitudeLogger } from 'amplitude';
import { AmplitudeErpDealEvent } from 'collections/amplitudeEvents';
import { action, computed, observable, runInAction } from 'mobx';
import { RootStore } from 'stores/RootStore';

export type DealLeftSidebarLayoutMode = 'fixed' | 'floating';

export type DealSidebarLayoutDisplay = 'full' | 'compact' | 'peek';

const STORAGE_KEY = 'dealLeftSidebarLayoutMode';

const PEEK_DELAY_MS = 275;

const readMode = (): DealLeftSidebarLayoutMode => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);

    if (raw === 'floating' || raw === 'fixed') {
      return raw;
    }
  } catch (e) {
    console.error(e);
  }

  return 'fixed';
};

export class DealSidebarUiStore {
  readonly rootStore: RootStore;

  @observable layoutMode: DealLeftSidebarLayoutMode = readMode();

  @observable isPeekOpen = false;

  private peekTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
  }

  @computed get layoutDisplay(): DealSidebarLayoutDisplay {
    const whenFloating: DealSidebarLayoutDisplay = this.isPeekOpen ? 'peek' : 'compact';

    return this.layoutMode === 'fixed' ? 'full' : whenFloating;
  }

  @action setLayoutMode = (mode: DealLeftSidebarLayoutMode) => {
    const previous = this.layoutMode;

    if (previous === mode) return;

    this.layoutMode = mode;
    this.cancelPeekSchedule();
    this.isPeekOpen = false;

    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch (e) {
      console.error(e);
    }

    amplitudeLogger({
      event: AmplitudeErpDealEvent.DEAL_LEFT_SIDEBAR_LAYOUT,
      attrs: {
        isFloating: String(mode === 'floating'),
      },
    });
  };

  @action toggleLayoutMode = () => {
    this.setLayoutMode(this.layoutMode === 'fixed' ? 'floating' : 'fixed');
  };

  @action schedulePeekOpen = () => {
    if (this.layoutMode !== 'floating') return;

    this.cancelPeekSchedule();

    this.peekTimer = setTimeout(() => {
      runInAction(() => {
        this.isPeekOpen = true;
      });
    }, PEEK_DELAY_MS);
  };

  @action cancelPeekSchedule = () => {
    if (this.peekTimer) {
      clearTimeout(this.peekTimer);

      this.peekTimer = null;
    }
  };

  @action closePeek = () => {
    this.cancelPeekSchedule();

    this.isPeekOpen = false;
  };
}
